import * as React from 'react'
import { ReactElement, useEffect } from 'react'
import { useFormikContext } from 'formik'
import useEntity from '../../../hooks/useEntity'
import useAxios from '../../../hooks/useAxios'
import useHost from '../../../hooks/useHost'
import useUser from '../../../hooks/useUser'
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogTitle,
    DialogTrigger,
    Tooltip,
} from '@fluentui/react-components'
import { ArrowDownRegular, ArrowUpRegular, DismissRegular, LinkSquareRegular } from '@fluentui/react-icons'
import { FormContainerProps, Values } from './LinkEmailFolderDialog'
import FormContent from './FormContent'
import { useTranslation } from 'react-i18next'

export default function FormContainer(props: FormContainerProps): ReactElement {
    const { email: userEmail } = useUser()
    const { RowKey: hostKey } = useHost()
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, setFieldValue, submitForm } = useFormikContext<Values>()
    // const [file] = useEntity<FileListItemDto>(id => ({ url: `/email/${userEmail}/folder/${id}/file` }), values.id)
    const [parentFolderId] = useEntity<string>(email => ({ url: `/email/${email}/parent-folder/id` }), userEmail)

    useEffect(() => {
        if (parentFolderId.loaded) {
            setFieldValue('parentFolderId', parentFolderId.data)
        }
    }, [parentFolderId.data])

    return (
        <DialogBody>
            <DialogTitle>{translate('email.filing.synchronize.title')}</DialogTitle>
            <DialogContent>
                <FormContent {...props} />
            </DialogContent>
            <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                    <Button
                        appearance="transparent"
                        icon={<DismissRegular />}
                        onClick={() => props.toggleDialog(false)}
                    >
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
                <Tooltip
                    content={translate('email.filing.synchronize.link.tooltip')}
                    relationship="description"
                    withArrow
                >
                    <Button
                        appearance="primary"
                        icon={<LinkSquareRegular />}
                        type="submit"
                        onClick={() => {
                            props.setCallback(() => () => Promise.resolve())
                            submitForm()
                        }}
                    >
                        {translate('email.filing.synchronize.link')}
                    </Button>
                </Tooltip>
                {!values.create && (
                    <Tooltip
                        content={translate('email.filing.synchronize.import.tooltip')}
                        relationship="description"
                        withArrow
                    >
                        <Button
                            appearance="primary"
                            icon={<ArrowUpRegular />}
                            type="submit"
                            onClick={() => {
                                props.setCallback(
                                    () => (folderId: string, fileReference: string) =>
                                        axios.post(
                                            `/api/import-folder/`,
                                            { hostKey, reference: fileReference, userEmail, folderId },
                                            {
                                                baseURL: process.env.INBOXER_HOST,
                                                headers: { SuppressErrors: 'true' },
                                            },
                                        ),
                                )
                                submitForm()
                            }}
                        >
                            {translate('email.filing.synchronize.import')}
                        </Button>
                    </Tooltip>
                )}

                <Tooltip
                    content={translate('email.filing.synchronize.export.tooltip')}
                    relationship="description"
                    withArrow
                >
                    <Button
                        appearance="primary"
                        icon={<ArrowDownRegular />}
                        type="submit"
                        onClick={() => {
                            props.setCallback(
                                () => (folderId: string, fileReference: string) =>
                                    axios.post(
                                        '/api/synchronise-file/',
                                        { hostKey, reference: fileReference, userEmail },
                                        {
                                            baseURL: process.env.INBOXER_HOST,
                                            headers: { SuppressErrors: 'true' },
                                        },
                                    ),
                            )
                            submitForm()
                        }}
                    >
                        {translate('email.filing.synchronize.export')}
                    </Button>
                </Tooltip>
            </DialogActions>
        </DialogBody>
    )
}
